import { CREAM, FOREST_GREEN, GOLD } from "@/components/public/publicTheme";

export type BrandedPageLoaderProps = {
  label?: string;
  message?: string;
  minHeight?: "screen" | "section";
};

const DEFAULT_LABEL = "Grand & Kansas North";
const DEFAULT_MESSAGE = "Preparing the project experience";

/**
 * Full-page branded loader for lazy routes and auth checks.
 */
export function BrandedPageLoader({
  label = DEFAULT_LABEL,
  message = DEFAULT_MESSAGE,
  minHeight = "screen",
}: BrandedPageLoaderProps = {}) {
  const heightClass =
    minHeight === "screen" ? "min-h-screen min-h-[100dvh]" : "min-h-[50vh] min-h-[50dvh]";

  return (
    <div
      className={`${heightClass} flex items-center justify-center px-6`}
      style={{ background: CREAM }}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <span
        className="absolute w-px h-px p-0 -m-px overflow-hidden whitespace-nowrap border-0"
        style={{ clip: "rect(0, 0, 0, 0)", clipPath: "inset(50%)" }}
      >
        Loading Grand & Kansas North
      </span>

      <div className="flex flex-col items-center text-center max-w-xs sm:max-w-sm">
        <div
          className="relative mb-7 p-5 border branded-loader-frame"
          style={{
            borderColor: "rgba(42, 66, 117, 0.18)",
            borderRadius: 2,
          }}
          aria-hidden
        >
          <svg
            viewBox="0 0 72 56"
            className="w-[4.5rem] h-[3.5rem] sm:w-20 sm:h-16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              x="4"
              y="22"
              width="22"
              height="30"
              stroke={FOREST_GREEN}
              strokeWidth="1.25"
              strokeOpacity="0.85"
            />
            <rect
              x="28"
              y="12"
              width="18"
              height="40"
              stroke={FOREST_GREEN}
              strokeWidth="1.25"
              strokeOpacity="0.65"
            />
            <rect
              x="48"
              y="18"
              width="20"
              height="34"
              stroke={FOREST_GREEN}
              strokeWidth="1.25"
              strokeOpacity="0.85"
            />
            <line
              x1="2"
              y1="52"
              x2="70"
              y2="52"
              stroke={GOLD}
              strokeWidth="1.25"
              strokeOpacity="0.9"
            />
            <line
              x1="36"
              y1="4"
              x2="36"
              y2="52"
              stroke={FOREST_GREEN}
              strokeWidth="0.75"
              strokeOpacity="0.2"
              strokeDasharray="3 4"
            />
          </svg>
        </div>

        <p
          className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.28em]"
          style={{ color: GOLD }}
        >
          El Segundo · Smoky Hollow
        </p>
        <h1
          className="mt-2 text-2xl sm:text-[1.75rem] font-extrabold leading-tight tracking-tight"
          style={{ color: FOREST_GREEN }}
        >
          {label}
        </h1>
        <p className="mt-3 text-xs sm:text-sm font-medium" style={{ color: "rgba(24, 45, 78, 0.55)" }}>
          {message}
        </p>

        <div
          className="mt-7 w-36 sm:w-40 h-px overflow-hidden rounded-full"
          style={{ background: "rgba(42, 66, 117, 0.12)" }}
          aria-hidden
        >
          <div
            className="h-full w-2/5 rounded-full branded-loader-line"
            style={{ background: GOLD }}
          />
        </div>
      </div>
    </div>
  );
}
